import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material";
import { auth } from "../../api/fbConfig"; // Firebase Authentication
import { getUserLinkedAccounts, linkCurrentAccount } from "../../api/api"; // API Calls
import { signInWithEmailAndPassword } from "firebase/auth";

const LinkAccounts = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [linkedAccounts, setLinkedAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  useEffect(() => {
    const fetchUser = async () => {
      if (!auth.currentUser) return;

      try {
        const userData = await getUserLinkedAccounts(auth.currentUser.email);
        setCurrentUser(auth.currentUser);
        setLinkedAccounts(userData.linkedEmails || []);
      } catch (error) {
        console.error("Error fetching user data:", error);
      } finally {
        setLoading(false);
      }
    };

    auth.onAuthStateChanged((user) => {
      if (user) {
        fetchUser();
      }
    });
  }, []);

  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEmail("");
    setPassword("");
  };

  const handleLinkAccount = async () => {
    if (!email || !password) return;

    setLinking(true);
    try {
      // Sign in with provided credentials
      const credential = await signInWithEmailAndPassword(auth, email, password);
      const newEmail = credential.user.email;

      // Send to API to link accounts
      await linkCurrentAccount(newEmail);
      setLinkedAccounts([...linkedAccounts, newEmail]);

      handleCloseDialog();
    } catch (error) {
      console.error("Error linking account:", error);
      alert("Failed to link account. Please check your credentials.");
    }
    setLinking(false);
  };

  if (loading) {
    return (
      <Container sx={{ textAlign: "center", mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container sx={{ mt: 4 }}>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5">Logged In As</Typography>
          <Typography variant="body1"><strong>Email:</strong> {currentUser?.email}</Typography>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5">Linked Accounts</Typography>
          {linkedAccounts.length > 0 ? (
            <List>
              {linkedAccounts.map((email, index) => (
                <ListItem key={index}>
                  <ListItemText primary={email} />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2">No linked accounts.</Typography>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h5">Link Another Account</Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenDialog}
            disabled={linking}
          >
            {linking ? "Linking..." : "Enter Credentials"}
          </Button>
        </CardContent>
      </Card>

      {/* Dialog for entering credentials */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog}>
        <DialogTitle>Enter Account Credentials</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sign in to the account you want to link.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Password"
            type="password"
            fullWidth
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleLinkAccount} color="primary" disabled={linking}>
            {linking ? "Linking..." : "Link Account"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default LinkAccounts;
